import { ISOCountryCode } from '@pmi.web/countries';
import { useCallback, useMemo, useState } from 'react';

import { useDebounceSearch } from './useDebounce';
import { getGlobalAddressSuggestions } from '../services/AddressLookupService';
import { IAddress } from '../types/IAddress';
import { formatAddressSuggestion } from '../utils/address-formatters/addressFormatter';

export function useGlobalAddressSuggestions(
  postalCode: string,
  locality: string,
  administrativeArea: string,
  country: ISOCountryCode,
  env: 'production' | 'development',
  suggestionsFormatter = formatAddressSuggestion,
) {
  const [suggestions, setSuggestions] = useState<ReadonlyArray<IAddress>>([]);

  const fetchSuggestions = useCallback(
    (searchInput: string) =>
      void getGlobalAddressSuggestions({
        address: searchInput,
        postalCode,
        locality,
        administrativeArea,
        country,
        env,
      })
        .then(setSuggestions)
        .catch(() => setSuggestions([])),
    [administrativeArea, country, env, locality, postalCode],
  );

  const fetchAddressSuggestions = useDebounceSearch(fetchSuggestions, 1);

  const addressSuggestions = useMemo(() => {
    return suggestions.map(s => ({
      label: suggestionsFormatter(s),
      value: s,
    }));
  }, [suggestions, suggestionsFormatter]);

  return {
    addressSuggestions,
    fetchAddressSuggestions,
  };
}
